import type {SafeHTMLString} from '@github-ui/safe-html'
import {UnsafeHTMLText} from '@github-ui/safe-html/UnsafeHTML'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {testIdProps} from '@github-ui/test-id-props'
import {useNamedColor} from '@github-ui/use-named-color'
import {Token, type TokenProps} from '@primer/react'
import {clsx} from 'clsx'
import type {Config} from 'dompurify'
import {forwardRef, type MouseEventHandler} from 'react'

import styles from './SingleSelectToken.module.css'
import type {NewSingleSelectOption} from './types'

interface SingleSelectTokenProps extends Omit<TokenProps, 'text'> {
  option: NewSingleSelectOption & {nameHtml?: SafeHTMLString}
  disabled?: boolean
}

/**
 * DOMPurify configuration for sanitizing user-supplied HTML in single-select option names.
 *
 * Security notes:
 * - `id` and `hidden` attributes are forbidden to prevent DOM island clobbering attacks
 *   on Memex JSON islands (e.g., 'stats-post-api-data'). User-created elements with IDs
 *   matching server-side islands could interfere with critical functionality.
 * - `style` and `class` are forbidden to prevent CSS injection and styling manipulation.
 */
export const DOMPURIFY_CONFIG: Readonly<Config> = {
  ALLOW_DATA_ATTR: false,
  ALLOWED_TAGS: ['a', 'g-emoji'],
  FORBID_ATTR: ['style', 'class', 'hidden', 'id'],
  KEEP_CONTENT: true,
}

export const SingleSelectToken = forwardRef<HTMLElement, SingleSelectTokenProps>(
  ({option, onClick: externalOnClick, disabled, ...tokenProps}, ref) => {
    const onClick: MouseEventHandler<HTMLSpanElement | HTMLButtonElement | HTMLAnchorElement> = event => {
      // Dont call handler if click target is a link. Really this is a hacky workaround for markup that isn't very good
      // (buttons should not contain interactive content like links) but we have to address this as a direct consequence
      // of our decision to allow links in single-select option values.
      if (event.target instanceof HTMLAnchorElement) event.stopPropagation()
      else externalOnClick?.(event)
    }

    const isInteractive = !!externalOnClick
    const color = useNamedColor(option.color)

    return (
      <Token
        className={clsx(styles.SingleSelectToken, {
          [styles.Interactive]: isInteractive,
          [styles.NonInteractive]: !isInteractive,
        })}
        style={{
          backgroundColor: color.bg,
          color: color.fg,
          borderColor: color.border,
          cursor: disabled ? 'not-allowed' : undefined,
        }}
        ref={ref}
        onClick={onClick}
        text={
          option.nameHtml ? (
            <VerifiedHTMLText html={option.nameHtml} />
          ) : (
            <UnsafeHTMLText html={option.name} domPurifyConfig={DOMPURIFY_CONFIG} />
          )
        }
        {...testIdProps('single-select-token')}
        {...tokenProps}
      />
    )
  },
)
SingleSelectToken.displayName = 'SingleSelectToken'
